import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Link from "next/link";
import {
	FaFacebook,
	FaGithub,
	FaInstagram,
	FaLinkedin,
	FaReddit,
	FaYoutube,
} from "react-icons/fa";
import { FaThreads, FaXTwitter } from "react-icons/fa6";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";
import { TooltipContent } from "../ui/tooltip-content";

export interface SocialLink {
	label: string;
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	style?: string;
	tooltip?: string;
}

const socialLinks: SocialLink[] = [
	{
		label: "Instagram",
		tooltip: "Follow on Instagram",
		href: "https://instagram.com/zeek.maps",
		icon: FaInstagram,
		style:
			"size-5 cursor-pointer hover:text-orange-600 hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "X",
		tooltip: "Follow on X",
		href: "https://x.com/zeekmaps",
		icon: FaXTwitter,
		style:
			"size-5 cursor-pointer hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "Facebook",
		tooltip: "Follow on Facebook",
		href: "https://facebook.com/zeekmap",
		icon: FaFacebook,
		style:
			"size-5 cursor-pointer hover:text-blue-600 hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "Youtube",
		tooltip: "Watch now",
		href: "https://youtube.com/@zeekmaps",
		icon: FaYoutube,
		style:
			"size-5 cursor-pointer hover:text-red-600 hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "Threads",
		tooltip: "Follow on Threads",
		href: "https://threads.com/@zeek.maps",
		icon: FaThreads,
		style:
			"size-5 cursor-pointer hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "Github",
		tooltip: "Follow on Github",
		href: "https://github.com/zeekmaps",
		icon: FaGithub,
		style:
			"size-5 cursor-pointer hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "LinkedIn",
		tooltip: "Follow on LinkedIn",
		href: "https://linkedin.com/company/zeek-maps",
		icon: FaLinkedin,
		style:
			"size-5 cursor-pointer hover:text-blue-500 hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
	{
		label: "Reddit",
		tooltip: "Follow on Reddit",
		href: "https://www.reddit.com/user/zeekmaps/",
		icon: FaReddit,
		style:
			"size-5 cursor-pointer hover:text-orange-500 hover:scale-125 transition delay-50 duration-100 ease-in-out",
	},
];

const variants = [
	{
		id: "default",
		titleNode: (
			<div className="flex flex-col items-center gap-2">
				<ZeekMapsIcon className="w-16 h-16" />
				<h2 className="text-center text-primary md:font-semibold">
					Join our community
					<br />
					<span className="text-shimmer">of mappers & explorers</span>
				</h2>
			</div>
		),
	},
	{
		id: "slim",
		titleNode: (
			<div className="flex flex-col gap-2 items-center">
				<ZeekMapsIcon className="w-14 h-auto" />
				<h2 className="text-md lg:text-lg text-primary">
					<span className="text-shimmer">Join the explorers</span>
				</h2>
			</div>
		),
	},
];

export default function CommunityCtaBox({
	className,
	variant = "default",
}: {
	className?: string;
	variant?: string;
}) {
	return (
		<div className={cn("p-2 flex items-center justify-center h-full")}>
			<div
				className={cn(
					"flex flex-col gap-1 justify-between w-full self-center",
					"p-4 justify-center text-center",
				)}
			>
				{variants.find((v) => v.id === variant)?.titleNode}

				<div className="flex md:self-center gap-3 mt-2">
					{socialLinks.map((link) => (
						<Button asChild key={link.label}>
							<TooltipContent content={link.tooltip}>
								<Link href={link.href} target="_blank" rel="noreferrer">
									<link.icon className={link.style} />
								</Link>
							</TooltipContent>
						</Button>
					))}
				</div>
			</div>
		</div>
	);
}
